import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from '../api/axios';

const statusOptions = ['ACTIVE', 'INACTIVE'];

export default function EmployeeForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [schedules, setSchedules] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [isDepartmentsLoading, setIsDepartmentsLoading] = useState(false);
  const [form, setForm] = useState({
    employeeCode: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    position: '',
    departmentId: '',
    workScheduleId: '',
    hireDate: '',
    status: 'ACTIVE',
  });
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(isEdit);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const loadEmployee = async () => {
      if (!isEdit) {
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const response = await axios.get(`/employees/${id}`);
        const employee = response.data;
        setForm({
          employeeCode: employee.employeeCode || '',
          firstName: employee.firstName || '',
          lastName: employee.lastName || '',
          email: employee.email || '',
          phone: employee.phone || '',
          position: employee.position || '',
          departmentId: employee.departmentId ?? '',
          workScheduleId: employee.workScheduleId ?? '',
          hireDate: employee.hireDate ? employee.hireDate.slice(0, 10) : '',
          status: employee.status || 'ACTIVE',
        });
      } catch (err) {
        const message =
          err.response?.data?.message || 'No se pudo cargar el empleado.';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    loadEmployee();
  }, [id, isEdit]);

  useEffect(() => {
    if (isEdit) {
      return;
    }
    const loadNextCode = async () => {
      try {
        const response = await axios.get('/employees/next-code');
        const nextCode = response.data?.nextCode;
        if (!nextCode) {
          return;
        }
        setForm((prev) =>
          prev.employeeCode ? prev : { ...prev, employeeCode: String(nextCode) },
        );
      } catch (err) {
        setError((prev) => prev || 'No se pudo obtener el código de empleado.');
      }
    };

    loadNextCode();
  }, [isEdit]);

  useEffect(() => {
    const loadSchedules = async () => {
      try {
        const response = await axios.get('/schedules');
        setSchedules(response.data);
      } catch (err) {
        setError((prev) => prev || 'No se pudieron cargar los horarios.');
      }
    };

    loadSchedules();
  }, []);

  useEffect(() => {
    const loadDepartments = async () => {
      setIsDepartmentsLoading(true);
      try {
        const response = await axios.get('/departments');
        setDepartments(response.data);
      } catch (err) {
        setError((prev) => prev || 'No se pudieron cargar los departamentos.');
      } finally {
        setIsDepartmentsLoading(false);
      }
    };

    loadDepartments();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setIsSaving(true);

    const payload = {
      employeeCode: form.employeeCode.trim(),
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim() || undefined,
      phone: form.phone.trim() || undefined,
      position: form.position.trim() || undefined,
      departmentId: form.departmentId ? Number(form.departmentId) : undefined,
      workScheduleId: form.workScheduleId ? Number(form.workScheduleId) : undefined,
      hireDate: form.hireDate || undefined,
      status: form.status,
    };

    try {
      if (isEdit) {
        await axios.patch(`/employees/${id}`, payload);
      } else {
        await axios.post('/employees', payload);
      }
      navigate('/home/empleados');
    } catch (err) {
      const message =
        err.response?.data?.message || 'No se pudo crear el empleado. Verifica los datos.';
      setError(message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800">
          {isEdit ? 'Editar empleado' : 'Alta de empleado'}
        </h1>
        <p className="text-sm text-gray-500">
          {isEdit ? 'Actualiza los datos del empleado.' : 'Completa los datos requeridos para registrar.'}
        </p>
      </div>

      <div className="rounded-lg bg-white p-6 shadow-sm">
        {isLoading ? (
          <div className="text-sm text-gray-500">Cargando empleado...</div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-semibold text-gray-600">Codigo</label>
                <input
                  type="text"
                  name="employeeCode"
                  value={form.employeeCode}
                  onChange={handleChange}
                  required={!isEdit}
                  className="mt-1 w-full rounded-lg border border-gray-200 p-2"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-600">Fecha de ingreso</label>
                <input
                  type="date"
                  name="hireDate"
                  value={form.hireDate}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-gray-200 p-2"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-600">Nombre</label>
                <input
                  type="text"
                  name="firstName"
                  value={form.firstName}
                  onChange={handleChange}
                  required={!isEdit}
                  className="mt-1 w-full rounded-lg border border-gray-200 p-2"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-600">Apellido</label>
                <input
                  type="text"
                  name="lastName"
                  value={form.lastName}
                  onChange={handleChange}
                  required={!isEdit}
                  className="mt-1 w-full rounded-lg border border-gray-200 p-2"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-600">Correo</label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-gray-200 p-2"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-600">Telefono</label>
                <input
                  type="text"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-gray-200 p-2"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-600">Cargo</label>
                <input
                  type="text"
                  name="position"
                  value={form.position}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-gray-200 p-2"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-600">Departamento</label>
                <select
                  name="departmentId"
                  value={form.departmentId}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-gray-200 p-2"
                  disabled={isDepartmentsLoading}
                >
                  {isDepartmentsLoading ? (
                    <option value="">Cargando departamentos...</option>
                  ) : (
                    <>
                      <option value="">Sin departamento</option>
                      {departments.map((department) => (
                        <option key={department.id} value={department.id}>
                          {department.code} - {department.name}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-600">Horario de trabajo</label>
                <select
                  name="workScheduleId"
                  value={form.workScheduleId}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-gray-200 p-2"
                >
                  <option value="">Sin horario</option>
                  {schedules.map((schedule) => (
                    <option key={schedule.id} value={schedule.id}>
                      {schedule.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-600">Estado</label>
                <select
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-gray-200 p-2"
                >
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {error && <div className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-600">{error}</div>}

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={isSaving}
                className="rounded-lg bg-blue-600 px-4 py-2 text-white transition hover:bg-blue-700 disabled:opacity-60"
              >
                {isSaving ? 'Guardando...' : 'Guardar'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/home/empleados')}
                className="rounded-lg border border-gray-200 px-4 py-2 text-gray-600 hover:bg-gray-50"
              >
                Cancelar
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
